import { useContext, useEffect } from "react";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { MoreContext } from "@/contexts/MoreContext";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User,
  FindUser,
  UserProducts,
  QueryOptions,
  LoginActivity,
  EventMaterials,
  UserIniPayments,
  ReferralResponse,
  UserAvailabilities,
  DefaultErrorResponse,
  GetChangedEventsInput,
  GetChangedEventsResponse,
  UserNotificationPaginator,
} from "@/types";
import "dayjs/locale/it";
import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/de";
import "dayjs/locale/fr";
import "dayjs/locale/pt";
import i18n from "@/utils/i18n";
import dayjs from "dayjs";
import client from "./client";
import routes from "@/config/routes.ts";
import useAuth from "@/utils/hooks/useAuth.ts";
import API_ENDPOINTS from "./client/endpoints";

export function useMe() {
  const { setIsBeta } = useContext(MoreContext);
  const { isAuthorized } = useAuth();

  const { data, isLoading, error } = useQuery<User, Error>(
    [API_ENDPOINTS.USERS_ME],
    client.users.me,
    { enabled: isAuthorized },
  );

  const lang = data?.item?.user?.lang;
  if (lang && (i18n.language !== lang || dayjs.locale() !== lang)) {
    void i18n.changeLanguage(lang || "en");
    dayjs.locale(lang || "en");
  }

  useEffect(() => {
    setIsBeta(Boolean(data?.item?.user?.settings?.isBeta));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  return {
    error,
    isLoading,
    isAuthorized,
    me: data,
  };
}

export function useLogout() {
  const { unauthorize } = useAuth();

  const queryClient = useQueryClient();

  return useMutation(client.users.logout, {
    onSuccess: () => {
      unauthorize();
      void queryClient.resetQueries([API_ENDPOINTS.USERS_ME]);
    },
  });
}

export function useLoginMutation() {
  return useMutation(client.users.login, {
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useRegisterMutation(callback?: () => void) {
  return useMutation(client.users.register, {
    onSuccess: (data) => {
      if (data.status !== 200) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(data.message, {
        variant: "success",
      });
      if (callback) callback();
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function usePasswordResetMutation() {
  return useMutation(client.users.resetPassword, {
    onSuccess: (data) => {
      if (data.status !== 200) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar("Password reset link sent to your email", {
        variant: "success",
      });
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useNewPasswordMutation() {
  const navigate = useNavigate();

  return useMutation(client.users.newPassword, {
    onSuccess: (data) => {
      if (data.status !== 200) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(data.message, {
        variant: "success",
      });
      setTimeout(() => {
        navigate(routes.login);
      }, 500);
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

//beta

export function usePasswordResetBetaMutation() {
  return useMutation(client.users.resetPasswordBeta, {
    onSuccess: (data) => {
      if (data.status !== 200) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar("Password reset link sent to your email", {
        variant: "success",
      });
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useNewPasswordBetaMutation() {
  const navigate = useNavigate();

  return useMutation(client.users.newPasswordBeta, {
    onSuccess: (data) => {
      if (data.status !== 200) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(data.message, {
        variant: "success",
      });
      setTimeout(() => {
        navigate(routes.login);
      }, 500);
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}



export function useAvailabilities(options?: QueryOptions) {
  const { data, isLoading, error, refetch } = useQuery<
    UserAvailabilities,
    Error
  >([API_ENDPOINTS.AVAILABILITIES], client.users.availabilities, options);

  return {
    error,
    isLoading,
    reload: refetch,
    availabilities: data,
  };
}

export function useIniPayments() {
  const { data, isLoading, error } = useQuery<UserIniPayments, Error>(
    [API_ENDPOINTS.PAYMENTS],
    client.users.iniPayments,
  );

  return {
    error,
    isLoading,
    payments: data,
  };
}

export function useUserProducts() {
  const { data, isLoading, error } = useQuery<UserProducts, Error>(
    [API_ENDPOINTS.USER_PRODUCTS],
    client.users.products,
  );

  return {
    error,
    isLoading,
    products: data,
  };
}

export function useUserEventsAndMaterials() {
  const { data, isLoading, error } = useQuery<EventMaterials, Error>(
    [API_ENDPOINTS.EVENTS_AND_MATERIALS],
    () => client.users.eventMaterials(),
    {
      refetchInterval: 1800000,
      refetchOnWindowFocus: !location.href.includes("pot"),
    },
  );

  return {
    error,
    isLoading,
    eventMaterials: data,
  };
}
export function useLoginActivity(token: string) {
  const { data, isLoading, error } = useQuery<LoginActivity, Error>(
    [API_ENDPOINTS.USER_LOGIN_ACTIVITY(token)],
    () => client.users.loginActivity(token),
  );

  const activity = dayjs(data?.item?.login_date);
  const diffInWeeks = Math.floor(dayjs().diff(activity, "week"));

  return {
    error,
    isLoading,
    activity: { diffInWeeks, ...data },
  };
}

export function useNotifications() {
  const { data, isLoading, error } = useQuery<UserNotificationPaginator, Error>(
    [API_ENDPOINTS.NOTIFICATIONS],
    client.users.notifications,
  );

  return {
    error,
    isLoading,
    notifications: data,
  };
}

export function useSetNotificationMetadataAsSeen() {
  const queryClient = useQueryClient();

  return useMutation(client.users.setNotificationMetadataAsSeen, {
    onSettled: () => {
      void queryClient.invalidateQueries([
        API_ENDPOINTS.NOTIFICATIONS_SET_SEEN,
      ]);
    },
  });
}

export function useSaveOnboardingMutation() {
  const queryClient = useQueryClient();

  return useMutation(client.users.onboarding, {
    onSettled: () => {
      void queryClient.invalidateQueries([API_ENDPOINTS.USERS_ME]);
    },
  });
}

export function useReferrals() {
  const { data, isLoading, error } = useQuery<ReferralResponse, Error>(
    [API_ENDPOINTS.GET_USER_REFERRALS],
    client.users.referrals,
  );

  return {
    error,
    isLoading,
    referrals: data,
  };
}

export function useUserChangeEvents(params: GetChangedEventsInput) {
  const { data, isLoading, error } = useQuery<GetChangedEventsResponse, Error>(
    [API_ENDPOINTS.GET_CHANGED_EVENTS, params],
    () => client.users.getChangeEvents(params),
  );

  return {
    error,
    isLoading,
    events: data,
  };
}

export function useUserChangeEventsMutation() {
  const queryClient = useQueryClient();

  return useMutation(client.users.getChangeEvents, {
    onSettled: () => {
      void queryClient.invalidateQueries([API_ENDPOINTS.GET_CHANGED_EVENTS]);
    },
  });
}

export function useUserChangeEventMutation() {
  const queryClient = useQueryClient();

  return useMutation(client.users.getChangeEvent, {
    onSettled: () => {
      void queryClient.invalidateQueries([API_ENDPOINTS.GET_CHANGE_EVENT]);
    },
  });
}

export function useUserSetChangeEventsMutation(callback?: () => void) {
  const queryClient = useQueryClient();

  return useMutation(client.users.setChangeEvents, {
    onSuccess: (data) => {
      if (!data.status) {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar("Event changed successfully", {
        variant: "success",
      });

      if (callback) callback();
    },
    onSettled: () => {
      void queryClient.invalidateQueries([API_ENDPOINTS.GET_CHANGED_EVENTS]);
      void queryClient.invalidateQueries([API_ENDPOINTS.EVENTS_AND_MATERIALS]);
    },
  });
}

export function useDeleteUserEvent(token: string, callback?: () => void) {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const data: unknown = await client.agenda.deleteEvent(id, token);
      return data;
    },
    {
      onSuccess: (data: unknown) => {
        if (!data) {
          return;
        }
        enqueueSnackbar("Event deleted successfully", {
          variant: "success",
        });

        if (callback) callback();
      },
      onSettled: () => {
        void queryClient.invalidateQueries([API_ENDPOINTS.GET_CHANGED_EVENTS]);
        void queryClient.invalidateQueries([
          API_ENDPOINTS.EVENTS_AND_MATERIALS,
        ]);
      },
    },
  );
}

export function useFindUser(token: string) {
  const { data, isLoading, error } = useQuery<FindUser, Error>(
    [API_ENDPOINTS.FIND_USER(token)],
    () => client.users.find(token),
    {
      retry: 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    error,
    isLoading,
    user: data,
  };
}

export function useUpdateTimezone() {
  return useMutation(client.users.updateTimezone, {
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      throw error;
    },
  });
}
